/**
 * The outcome taxonomy — the single source for the SQL CHECK constraints, the review
 * schema and the analytics. How its values are shown lives in `outcome-display.ts`, so
 * nothing here depends on the UI.
 *
 * Nothing in Postgres can import this file, so the CHECK values are copied literally into
 * the migration. `tests/integration/conversation-quality-taxonomy.db.test.ts` inserts every
 * value listed here and rejects one that is not, which is what keeps the two spellings
 * honest — a mismatch would otherwise surface as a write failing in production for a value
 * the code considers valid.
 */

export const SESSION_OUTCOMES = [
  "resolved_positive",
  "resolved_neutral",
  "escalated",
  "unresolved_left",
  "abandoned",
  "spam",
] as const
export type SessionOutcome = (typeof SESSION_OUTCOMES)[number]

/**
 * The customer got what they came for. A verdict, not an attribution: self-service also needs
 * the agent to have handled the session alone — see `selfServiceShare`.
 */
export const RESOLVED_OUTCOMES: SessionOutcome[] = ["resolved_positive", "resolved_neutral"]

/** Excluded from every quality percentage — noise is volume, not service. */
export const NON_ACTIONABLE_OUTCOMES: SessionOutcome[] = ["spam"]

/** What every outcome rate divides by: verdicts on something that was a request at all. */
export const REQUEST_OUTCOMES: SessionOutcome[] = SESSION_OUTCOMES.filter(
  (outcome) => !NON_ACTIONABLE_OUTCOMES.includes(outcome)
)

export const ESCALATION_CATEGORIES = [
  "knowledge_gap",
  "out_of_scope_action",
  "customer_preference",
  "emotional",
  "policy_boundary",
  "other",
] as const
export type EscalationCategory = (typeof ESCALATION_CATEGORIES)[number]

/** A handover by design — a rule, the customer's wish, their feelings — rather than a gap the agent could close. */
export const INTENDED_HANDOVER_CATEGORIES: EscalationCategory[] = ["policy_boundary", "customer_preference", "emotional"]

export const TAKEOVER_REASONS = [
  "ai_answered_poorly",
  "customer_frustrated",
  "sales_opportunity",
  "no_visible_reason",
] as const
export type TakeoverReason = (typeof TAKEOVER_REASONS)[number]

/** How the human phase ended. `unhandled` means nobody ever replied. */
export const HUMAN_OUTCOMES = [
  "resolved_positive",
  "resolved_neutral",
  "unresolved_left",
  "unhandled",
] as const
export type HumanOutcome = (typeof HUMAN_OUTCOMES)[number]

/** How many topics a review may store on one session. */
export const MAX_TOPICS_PER_SESSION = 3

/** One list for every tenant, so questions compare across key spellings. Lives in the `topics` jsonb: no CHECK copies it. */
export const TOPIC_CATEGORIES = [
  "product_information",
  "product_fit",
  "pricing_and_payment",
  "availability",
  "orders_and_delivery",
  "changes_and_returns",
  "booking",
  "account_and_access",
  "technical_issue",
  "business_inquiry",
  "contact_request",
  "other",
] as const
export type TopicCategory = (typeof TOPIC_CATEGORIES)[number]

export const REVIEW_STATUSES = ["pending", "deferred", "done", "skipped", "failed"] as const
export type ReviewStatus = (typeof REVIEW_STATUSES)[number]

export const OUTCOME_SOURCES = ["rule", "ai", "manual"] as const
export type OutcomeSource = (typeof OUTCOME_SOURCES)[number]

/** Sessions per outcome. */
export type OutcomeCounts = Record<SessionOutcome, number>

/**
 * Sessions per outcome, split by who handled them. The agent's own rates need the split: a
 * verdict says how a session ended, not whether the agent was the one who ended it.
 */
export interface CohortOutcomeCounts {
  aiOnly: OutcomeCounts;
  teamInvolved: OutcomeCounts;
  /** Nobody replied at all — the agent's to answer, and nobody's success. */
  unattended: OutcomeCounts;
}

/** The cohorts in the order «Who handled it» reads them: the agent's share first, what nobody touched last. */
export const COHORTS = ["aiOnly", "teamInvolved", "unattended"] as const

export type Cohort = (typeof COHORTS)[number]

/** Whether a value read from the database belongs to one of the lists above. */
export function isOneOf<T extends string>(
  values: readonly T[],
  value: string | null | undefined
): value is T {
  return typeof value === "string" && (values as readonly string[]).includes(value)
}
